import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/payFileApprove.mutation"

import { PAY_FILE_APPROVED_WARNING_TOOLTIP } from "../constants"
import { updatePayFileInSearchStore } from "../stores/usePayFileSearchStore"
import { getPayFileFailure } from "../utils"

import { useWaitForPayFileProcessing } from "./useWaitForPayFileProcessing"

import type {
  ApprovePayFileMutation,
  ApprovePayFileMutationVariables,
  PayFileApproveFailure,
  PayFileApproveInput,
} from "@/graphql/codegen/graphql"
import { ApprovePayFileDocument, PayFileStatus } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const PAY_FILE_FAILURE_NOTIFICATION_DURATION_MS = 8000

export interface UseApprovePayFileReturn {
  approvePayFile: (input: PayFileApproveInput) => Promise<boolean>
  isApproving: boolean
}

export const useApprovePayFile = (): UseApprovePayFileReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<ApprovePayFileMutation, ApprovePayFileMutationVariables>(
    ApprovePayFileDocument
  )

  const { waitForProcessing } = useWaitForPayFileProcessing()

  const approvePayFile = useCallback(
    async (input: PayFileApproveInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.payFile.approve
      const hasError = error || output?.__typename === "PayFileApproveFailure"

      if (hasError) {
        const message = error?.message ?? (output as PayFileApproveFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }

      // Approving only moves the pay file to Processing; the CSV is generated and sent afterwards, so
      // the result is reported once that has finished instead of on this response.
      void waitForProcessing(input.payFileId).then((outcome) => {
        if (!outcome) return

        updatePayFileInSearchStore(input.payFileId, outcome)

        if (outcome.status === PayFileStatus.Sent) {
          notify({ type: NotificationType.Success, message: "Pay file sent" })
        }

        // Back to Approved means the send failed; Cancelled or Pending already show in the status.
        if (outcome.status === PayFileStatus.Approved) {
          const failure = getPayFileFailure(outcome.latestLog)
          notify({
            type: NotificationType.Error,
            message: failure
              ? [failure.title, failure.description].filter(Boolean).join(" ")
              : PAY_FILE_APPROVED_WARNING_TOOLTIP,
            autoHideDuration: PAY_FILE_FAILURE_NOTIFICATION_DURATION_MS,
          })
        }
      })

      return true
    },
    [executeMutation, notify, waitForProcessing]
  )

  return { approvePayFile, isApproving: fetching }
}
